import React, { useState, useEffect } from 'react';
import { attachmentsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Download, X, FileText, Image, Paperclip } from 'lucide-react';

export function AttachmentsDialog({ entryId, onClose, userRole }) {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [attachmentBlobs, setAttachmentBlobs] = useState({});
  const isEditor = ['admin', 'manager'].includes(userRole);

  useEffect(() => {
    if (entryId) {
      attachmentsApi.list(entryId).then(r => setAttachments(r.data)).catch(() => setAttachments([]));
      setAttachmentBlobs({});
    }
  }, [entryId]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !entryId) return;
    setUploading(true);
    try {
      await attachmentsApi.upload(entryId, file);
      const { data } = await attachmentsApi.list(entryId);
      setAttachments(data);
    } catch {}
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (fileId) => {
    try {
      await attachmentsApi.delete(fileId);
      setAttachments(prev => prev.filter(a => a.file_id !== fileId));
    } catch {}
  };

  const loadBlob = async (att) => {
    if (attachmentBlobs[att.file_id]) return;
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/files/${att.storage_path}`, { credentials: 'include' });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      setAttachmentBlobs(prev => ({ ...prev, [att.file_id]: url }));
    } catch {}
  };

  return (
    <Dialog open={!!entryId} onOpenChange={() => { onClose(); setAttachments([]); setAttachmentBlobs({}); }}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Attachments</DialogTitle>
          <DialogDescription className="sr-only">Manage file attachments for this entry</DialogDescription>
        </DialogHeader>
        <div className="space-y-4" data-testid="attachments-dialog">
          {isEditor && (
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Upload Bill / Invoice</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Input type="file" accept="image/jpeg,image/png,image/webp,image/gif,application/pdf" onChange={handleUpload} disabled={uploading} data-testid="attachment-file-input" />
                {uploading && <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />}
              </div>
              <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WebP, GIF, PDF — Max 10MB</p>
            </div>
          )}
          {attachments.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground text-sm">
              <Paperclip className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No attachments yet
            </div>
          ) : (
            <div className="space-y-3">
              {attachments.map(att => {
                const isImage = att.content_type?.startsWith('image/');
                const blobUrl = attachmentBlobs[att.file_id];
                if (isImage && !blobUrl) loadBlob(att);
                return (
                  <div key={att.file_id} className="border border-border p-3 space-y-2" data-testid={`attachment-${att.file_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        {isImage ? <Image className="w-4 h-4 text-muted-foreground shrink-0" /> : <FileText className="w-4 h-4 text-muted-foreground shrink-0" />}
                        <span className="text-sm truncate">{att.original_filename}</span>
                        <span className="text-xs text-muted-foreground shrink-0">{att.size ? `${(att.size / 1024).toFixed(0)} KB` : ''}</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${att.storage_path}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-muted rounded" title="Download" data-testid={`download-attachment-${att.file_id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </a>
                        {isEditor && (
                          <button onClick={() => handleDelete(att.file_id)} className="p-1.5 hover:bg-muted rounded text-red-600" title="Delete" data-testid={`delete-attachment-${att.file_id}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    {isImage && blobUrl && (
                      <img src={blobUrl} alt={att.original_filename} className="max-h-48 w-auto rounded border border-border object-contain" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
